import React from "react";
import { useDashTheme } from "../../../hooks/useDashTheme";
import { ToolIcon } from "../../../components/classic/panels";
import { SERVER_URL } from "../../../../../utils/serverConfig";
import { CATS, fmtDur, fmtClockTz, hourLabel } from "../bits";
import { DrawerSec, DrawerSpine } from "./StoryDrawer";
import { PointLines, InsightCallout, Sk } from "./storyBits";

/* One hour, in depth — the drawer body behind a row of the hour grid.
   ─────────────────────────────────────────────────────────────────────────
   Two layers, same contract as the other deep reads: the measured record
   streams first (facts — instant, deterministic), then the written account
   (headline → points → key insight). Every clock time is formatted with the
   org offset the server resolved; the browser's zone is never consulted. */

function HourSkeleton({ tone }) {
  return (
    <div>
      <Sk w="82%" h={16} style={{ marginBottom: 10 }} />
      <Sk w="60%" h={16} style={{ marginBottom: 26 }} />
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ display: "flex", gap: 12, marginBottom: 14 }}>
          <span style={{ width: 8, height: 8, borderRadius: 99, background: `${tone}44`, flex: "none", marginTop: 5 }} />
          <span style={{ flex: 1 }}>
            <Sk w={i % 2 ? "52%" : "68%"} h={13} />
            <Sk w="80%" h={11} style={{ marginTop: 7 }} />
          </span>
        </div>
      ))}
    </div>
  );
}

export function HourDeepRead({ hour, userId, date, source, offsetMin, bounds, onOpenThread }) {
  const t = useDashTheme();
  const tone = t.c.primary;
  const [facts, setFacts] = React.useState(null);
  const [headline, setHeadline] = React.useState("");
  const [points, setPoints] = React.useState([]);
  const [insight, setInsight] = React.useState("");
  const [running, setRunning] = React.useState(true);
  const [error, setError] = React.useState(null);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (startedRef.current) return;   // one call per open, not per render
    startedRef.current = true;
    const ctrl = new AbortController();

    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/hour-detail`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ userId, date, source, hour: hour.hour }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (err?.error?.code === "AI_LIMIT_REACHED") throw new Error("AI limit reached for this organisation.");
          throw new Error(err?.message || "Could not open this hour.");
        }
        const reader = res.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() || "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            let d; try { d = JSON.parse(line.slice(6)); } catch (_) { continue; }
            if (d.type === "facts") setFacts(d.facts);
            else if (d.type === "headline") setHeadline(d.text);
            else if (d.type === "point") setPoints((c) => [...c, d.text]);
            else if (d.type === "insight") setInsight(d.text);
            else if (d.type === "error") setError(d.message);
            else if (d.type === "done") setRunning(false);
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") setError(e.message);
      } finally { setRunning(false); }
    })();

    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error && !facts) {
    return (
      <div style={{
        fontSize: t.font.size.md, color: t.status.negative, background: t.status.negativeSoft,
        padding: "10px 12px", borderRadius: 9,
      }}>{error}</div>
    );
  }
  if (!facts) return <HourSkeleton tone={tone} />;

  const clock = (ms) => fmtClockTz(ms, offsetMin);
  const srcLabel = (s) => (s === "agent" ? "background agent" : "desktop app");

  // Rows for minute-by-minute: stretches and real gaps interleaved by time.
  const feed = [
    ...(facts.blocksList || []).map((b) => ({ kind: "block", at: b.startMs, b })),
    ...(facts.gaps || []).map((g) => ({ kind: "gap", at: g.startMs, g })),
  ].sort((a, b) => a.at - b.at);

  return (
    <div>
      {/* ── the hour in one line ── */}
      <DrawerSec title="The hour in one line">
        {headline ? (
          <div style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.65, maxWidth: "68ch", color: t.c.text }}>{headline}</div>
        ) : running ? (
          <Sk w="78%" h={16} />
        ) : (
          <div style={{ fontSize: 16, fontWeight: 500, lineHeight: 1.65, color: t.c.text }}>
            {fmtDur(facts.trackedMin)} tracked across {(facts.apps || []).length} application{(facts.apps || []).length === 1 ? "" : "s"}.
          </div>
        )}
      </DrawerSec>

      {/* ── where it sat in the day ── */}
      {bounds && (
        <DrawerSec title="Where it sat in the day">
          <DrawerSpine
            spans={[{ startMs: Math.max(facts.hourStartMs, bounds.startMs), endMs: Math.min(facts.hourEndMs, bounds.endMs) }]}
            bounds={bounds} tone={tone}
            startLabel={clock(bounds.startMs)} endLabel={clock(bounds.endMs)}
          />
        </DrawerSec>
      )}

      {/* ── minute by minute ── */}
      <DrawerSec title="Minute by minute">
        {feed.map((row, i) => row.kind === "gap" ? (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", color: t.c.textTertiary, fontSize: 12.5, fontWeight: 500 }}>
            <span style={{ flex: 1, borderTop: `1.5px dashed ${t.c.border}` }} />
            {row.g.minutes}m away — no activity recorded
            <span style={{ flex: 1, borderTop: `1.5px dashed ${t.c.border}` }} />
          </div>
        ) : (
          <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "12px 0", borderBottom: i < feed.length - 1 ? `1px solid ${t.c.border}` : "none" }}>
            <div style={{ width: 122, flex: "none", fontSize: 12.5, fontWeight: 600, color: t.c.textSecondary, lineHeight: 1.5, fontVariantNumeric: "tabular-nums" }}>
              {clock(row.b.startMs)} – {clock(row.b.endMs)}
              <span style={{ display: "block", fontWeight: 500, color: t.c.textTertiary, fontSize: 11 }}>
                {row.b.minutes}m · {srcLabel(row.b.src)}
              </span>
            </div>
            <span style={{
              width: 32, height: 32, borderRadius: 10, flex: "none", display: "grid", placeItems: "center",
              background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`,
            }}>
              <ToolIcon name={row.b.app} cat={row.b.cat} size={18} />
            </span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.c.text }}>{row.b.app}</div>
              {(row.b.title || row.b.url) && (
                <div style={{ fontSize: 13, lineHeight: 1.55, color: t.c.textSecondary, marginTop: 2, maxWidth: "60ch", overflowWrap: "anywhere" }}>
                  {row.b.title || row.b.url}
                </div>
              )}
            </div>
            <span style={{ flex: "none", fontSize: 11, fontWeight: 600, color: t.c.textTertiary, padding: "4px 10px", borderRadius: 99, background: t.c.surfaceMuted }}>
              {row.b.activityLabel}
            </span>
          </div>
        ))}
      </DrawerSec>

      {/* ── the written account ── */}
      {(points.length > 0 || running) && (
        <DrawerSec title="What the work was">
          {points.length ? <PointLines points={points} tone={tone} /> : <HourSkeleton tone={tone} />}
        </DrawerSec>
      )}

      {/* ── artifacts ── */}
      {(facts.artifacts || []).length > 0 && (
        <DrawerSec title="Artifacts touched">
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {facts.artifacts.map((a) => (
              <span key={a} style={{
                display: "inline-flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 600,
                color: t.c.textSecondary, padding: "8px 13px", borderRadius: 10,
                background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`, margin: "0 8px 8px 0",
                maxWidth: "100%", overflowWrap: "anywhere",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flex: "none" }}>
                  <path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4" />
                </svg>
                {a}
              </span>
            ))}
          </div>
        </DrawerSec>
      )}

      {/* ── link back to the day's chapters ── */}
      {(facts.threadsTouched || []).length > 0 && (
        <DrawerSec title="Part of these chapters">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {facts.threadsTouched.map((th) => (
              <button
                key={th.key} type="button" className="st-reset st-focusable st-hit"
                onClick={onOpenThread ? () => onOpenThread(th.key) : undefined}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600,
                  color: t.c.text, padding: "9px 15px", borderRadius: 11, cursor: onOpenThread ? "pointer" : "default",
                  background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`,
                }}
              >
                <i style={{ width: 7, height: 7, borderRadius: "50%", background: t.c.primary }} />
                {th.title}
                <span style={{ color: t.c.textTertiary, fontWeight: 500 }}>· {fmtDur(th.dayMinutes)} today</span>
              </button>
            ))}
          </div>
          {onOpenThread && (
            <div style={{ fontSize: 12, fontWeight: 500, color: t.c.textTertiary, marginTop: 9 }}>
              Opens the chapter's full story.
            </div>
          )}
        </DrawerSec>
      )}

      {/* ── against this day's average hour ── */}
      {facts.dayAvg && (
        <DrawerSec title="Against this day's average hour">
          <div style={{ display: "grid", gridTemplateColumns: "130px 1fr 110px", gap: "10px 14px", alignItems: "center", maxWidth: 560 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: t.c.textSecondary }}>Tracked</span>
            <span style={{ position: "relative", height: 20 }}>
              <b style={{ position: "absolute", top: 0, left: 0, height: 8, borderRadius: 4, background: tone, width: `${Math.min(100, (facts.trackedMin / 60) * 100)}%` }} />
              <b style={{ position: "absolute", top: 11, left: 0, height: 8, borderRadius: 4, background: t.c.textTertiary, opacity: 0.4, width: `${Math.min(100, (facts.dayAvg.trackedMin / 60) * 100)}%` }} />
            </span>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: t.c.textTertiary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {facts.trackedMin}m · avg {facts.dayAvg.trackedMin}m
            </span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: t.c.textTertiary, marginTop: 10 }}>
            Coloured — this hour · grey — the average tracked hour of this day.
          </div>
        </DrawerSec>
      )}

      {/* ── attention profile ── */}
      <DrawerSec title="Attention profile">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, maxWidth: 560 }}>
          {[
            [`${facts.activity}%`, "of the hour active"],
            [fmtDur(facts.longestRunMin), "longest unbroken run"],
            [String((facts.gaps || []).length), (facts.gaps || []).length === 1 ? "interruption" : "interruptions"],
          ].map(([big, small]) => (
            <div key={small} style={{ background: t.c.surfaceMuted, border: `1px solid ${t.c.border}`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ fontSize: 21, fontWeight: 700, letterSpacing: "-0.02em", color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{big}</div>
              <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: "0.09em", textTransform: "uppercase", color: t.c.textTertiary, marginTop: 4 }}>{small}</div>
            </div>
          ))}
        </div>
      </DrawerSec>

      {insight && <InsightCallout text={insight} tone={tone} />}
    </div>
  );
}

/** Deterministic drawer-header bits for an hour bucket — used by the page. */
export function hourDrawerProps(h, C, icon) {
  const src = h.source === "Both"
    ? "Desktop app + background agent"
    : h.source === "Agent" ? "Background agent" : "Desktop app";
  return {
    tone: C[h.domCat] || C.browse,
    kicker: `Hour · ${CATS[h.domCat]?.label || h.domCat}`,
    title: `${hourLabel(h.hour)} — ${h.apps[0]?.name || "the hour"}`,
    chips: [
      `${h.trackedMin}m tracked of 60`,
      src,
      h.gaps.length ? `${h.gaps.length} interruption${h.gaps.length === 1 ? "" : "s"}` : null,
      h.apps.slice(0, 3).map((a) => a.name).join(" · ") || null,
    ],
    icon,
  };
}
